import { useCallback, useMemo, useState } from 'react';
const INITIAL_STATE = { path: [], direction: 'none' };
const hasChildren = (item) => Boolean(item.children);
/**
 * Walks `path` through the item tree and returns the branches it resolves to. Resolution stops at the first id that
 * is no longer present, which keeps the menu on the deepest level that still exists after the consumer replaces its
 * items.
 */
const resolveTrail = (items, path) => {
    var _a, _b;
    const trail = [];
    let levelItems = items;
    for (const id of path) {
        const branch = levelItems.find((item) => item.id === id && hasChildren(item));
        if (!branch) {
            break;
        }
        trail.push(branch);
        levelItems = (_b = (_a = branch.children) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : [];
    }
    return trail;
};
/**
 * Drill-down navigation for nested dropdown options.
 *
 * The path is kept as ids rather than item objects so that every render resolves against the items the consumer
 * currently passes. Asynchronously loaded children therefore reach the open level without any extra synchronisation.
 */
export const useDropdownNavigation = (items) => {
    var _a, _b;
    const [navigation, setNavigation] = useState(INITIAL_STATE);
    const { direction, focusItemId, path } = navigation;
    const trail = useMemo(() => resolveTrail(items, path), [items, path]);
    const branch = trail[trail.length - 1];
    const levelItems = branch ? ((_b = (_a = branch.children) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : []) : items;
    const depth = trail.length;
    const drillInto = useCallback((item) => {
        var _a, _b;
        if (!hasChildren(item)) {
            return;
        }
        setNavigation((current) => ({ path: [...current.path, item.id], direction: 'forward' }));
        (_b = (_a = item.children) === null || _a === void 0 ? void 0 : _a.onOpen) === null || _b === void 0 ? void 0 : _b.call(_a);
    }, []);
    const goBack = useCallback(() => {
        setNavigation((current) => {
            if (!current.path.length) {
                return current;
            }
            return {
                path: current.path.slice(0, -1),
                direction: 'backward',
                focusItemId: current.path[current.path.length - 1],
            };
        });
    }, []);
    const reset = useCallback(() => setNavigation((current) => (current.path.length || current.direction !== 'none' ? INITIAL_STATE : current)), []);
    return {
        /** The branch the menu is currently inside, or undefined at the root level. */
        branch,
        depth,
        direction,
        drillInto,
        focusItemId,
        goBack,
        /** Options rendered at the current level. */
        levelItems,
        /** Changes whenever the menu moves to another level, so the panel can remount and animate. */
        levelKey: path.slice(0, depth).join('/') || 'root',
        reset,
        trail,
    };
};
//# sourceMappingURL=use_dropdown_navigation.hook.js.map